import React, { useState } from "react";
import "./ExportModal.css";

// ── Field group definitions ───────────────────────────────────────────────────
// Single source of truth for every exportable field and its display label.
// "cannabinoids" and "terpenes" are group-level toggles, not individual columns.

export const FIELD_GROUPS = [
    {
        key: "product",
        label: "Product Info",
        fields: [
            { key: "product_name",  label: "Product Name" },
            { key: "sku",           label: "SKU" },
            { key: "product_type",  label: "Type" },
            { key: "color",         label: "Color" },
            { key: "published",     label: "Published" },
            { key: "quantity",      label: "Quantity" },
            { key: "weight_unit",   label: "Unit" },
            { key: "price",         label: "Price" },
            { key: "upload_date",   label: "Upload Date" },
        ],
    },
    {
        key: "computed",
        label: "Computed Values",
        fields: [
            { key: "total_thc",      label: "Total THC (%)" },
            { key: "total_terps",    label: "Total Terpenes (%)" },
            { key: "tp_ratio",       label: "T/P Ratio" },
            { key: "panel_complete", label: "Panel Complete" },
        ],
    },
    {
        key: "coa_meta",
        label: "COA Metadata",
        fields: [
            { key: "lab_name",                           label: "Lab Name" },
            { key: "lab_state",                          label: "Lab State" },
            { key: "lab_license_number",                 label: "Lab License #" },
            { key: "date_collected",                     label: "Date Collected" },
            { key: "date_received",                      label: "Date Received" },
            { key: "date_completed",                     label: "Date Completed" },
            { key: "sample_id",                          label: "Sample ID" },
            { key: "batch_id",                           label: "Batch ID" },
            { key: "batch_metrc_id",                     label: "Batch METRC ID" },
            { key: "sample_metrc_id",                    label: "Sample METRC ID" },
            { key: "batch_biotrack_id",                  label: "Batch BioTrack ID" },
            { key: "sample_biotrack_id",                 label: "Sample BioTrack ID" },
            { key: "cultivator_producer_name",           label: "Cultivator/Producer" },
            { key: "cultivator_producer_license_number", label: "Cultivator License" },
            { key: "distributor_name",                   label: "Distributor" },
            { key: "distributor_license_number",         label: "Distributor License" },
        ],
    },
    {
        key: "safety",
        label: "Safety Panels",
        fields: [
            { key: "microbials",      label: "Microbials" },
            { key: "pesticides",      label: "Pesticides" },
            { key: "heavy_metals",    label: "Heavy Metals" },
            { key: "mycotoxins",      label: "Mycotoxins" },
            { key: "foreign_matter",  label: "Foreign Matter" },
            { key: "moisture_content", label: "Moisture Content (%)" },
        ],
    },
    {
        key: "test_results",
        label: "Test Results",
        singleColumn: true,
        fields: [
            { key: "cannabinoids", label: "Cannabinoids (all)" },
            { key: "terpenes",     label: "Terpenes (all)" },
        ],
    },
];

// All field keys set to true — used as the default selection state
export const DEFAULT_EXPORT_FIELDS = FIELD_GROUPS
    .flatMap((g) => g.fields)
    .reduce((acc, f) => ({ ...acc, [f.key]: true }), {});

// ── ExportModal component ─────────────────────────────────────────────────────

export default function ExportModal({ onConfirm, onCancel }) {
    const [selected, setSelected] = useState({ ...DEFAULT_EXPORT_FIELDS });

    const toggle = (key) =>
        setSelected((prev) => ({ ...prev, [key]: !prev[key] }));

    const toggleGroup = (group) => {
        const keys = group.fields.map((f) => f.key);
        const allChecked = keys.every((k) => selected[k]);
        setSelected((prev) => {
            const next = { ...prev };
            keys.forEach((k) => { next[k] = !allChecked; });
            return next;
        });
    };

    const allKeys = FIELD_GROUPS.flatMap((g) => g.fields.map((f) => f.key));
    const allChecked  = allKeys.every((k) => selected[k]);
    const anyChecked  = allKeys.some((k) => selected[k]);

    const toggleAll = () => {
        const next = !allChecked;
        setSelected(allKeys.reduce((acc, k) => ({ ...acc, [k]: next }), {}));
    };

    return (
        <div className="em-overlay" onClick={onCancel}>
            <div className="em-modal" onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className="em-header">
                    <h2 className="em-title">Export Options</h2>
                    <button className="em-close" onClick={onCancel} aria-label="Close">✕</button>
                </div>

                {/* Global select-all */}
                <div className="em-top-actions">
                    <label className="em-select-all-label">
                        <input
                            type="checkbox"
                            checked={allChecked}
                            onChange={toggleAll}
                        />
                        <span>{allChecked ? "Deselect All" : "Select All"}</span>
                    </label>
                </div>

                {/* Field groups */}
                <div className="em-body">
                    {FIELD_GROUPS.map((group) => {
                        const groupKeys   = group.fields.map((f) => f.key);
                        const groupAllOn  = groupKeys.every((k) => selected[k]);
                        return (
                            <div key={group.key} className="em-group">
                                <div className="em-group-header">
                                    <label className="em-group-toggle">
                                        <input
                                            type="checkbox"
                                            checked={groupAllOn}
                                            onChange={() => toggleGroup(group)}
                                        />
                                        <span className="em-group-label">{group.label}</span>
                                    </label>
                                </div>
                                <div className={`em-fields${group.singleColumn ? " em-fields--single" : ""}`}>
                                    {group.fields.map((field) => (
                                        <label key={field.key} className="em-field">
                                            <input
                                                type="checkbox"
                                                checked={selected[field.key]}
                                                onChange={() => toggle(field.key)}
                                            />
                                            <span>{field.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="em-footer">
                    <button className="em-btn em-btn--cancel" onClick={onCancel}>
                        Cancel
                    </button>
                    <button
                        className="em-btn em-btn--export"
                        onClick={() => onConfirm(selected)}
                        disabled={!anyChecked}
                    >
                        Export
                    </button>
                </div>
            </div>
        </div>
    );
}
